import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import {
    persistStore,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
} from 'redux-persist';

import { authApi } from 'features/auth/services';
import { userApi } from 'features/user/services';
import { subscriptionsApi } from 'features/subscriptions/services';
import { communitiesAPi } from 'features/communities/services';
import { adminApi } from 'features/admin/store';

import rootReducer from './rootReducer';
import { rtkQueryErrorLogger } from './errorMiddleware';
import { monetizationApi } from '../features/monetization/services';
import { commonAiApi } from '../features/ai/services/ai';
import { chatsApi } from '../features/communities/components/Chats/service/chat.api';
import { analyticsApi } from '../features/analytics/store';
import { recommendationsAIApi } from '../features/communities/components/recommendations/service/recommendations-ai.api';
import { shopApi } from '../features/shops/store';
import { crmApi } from '../features/crm/store';
import { coursesApi } from '../features/courses/store';

export const store = configureStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [
                    FLUSH,
                    REHYDRATE,
                    PAUSE,
                    PERSIST,
                    PURGE,
                    REGISTER,
                ],
            },
        }).concat([
            rtkQueryErrorLogger,
            authApi.middleware,
            userApi.middleware,
            subscriptionsApi.middleware,
            communitiesAPi.middleware,
            chatsApi.middleware,
            ...adminApi,
            ...monetizationApi,
            ...shopApi,
            ...crmApi,
            ...coursesApi,
            ...analyticsApi,
            recommendationsAIApi.middleware,
            commonAiApi.middleware,
        ]),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
